import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { workOrderEditPath } from "../../../../api/projectPlan";

/**
 * Quick links while scheduling: edit project event, work order, project activity.
 */
export default function PlanScheduleContextBar({
  projectId,
  workOrder = null,
  projectActivity = null,
  schedulingMode = "project",
  onEditProjectEvent,
}) {
  const { t } = useTranslation();
  const pid = projectId || workOrder?.project_id || projectActivity?.project_id;
  const activityTabUrl = pid ? `/dashboard/projects/${pid}?tab=1` : null;
  const woEditUrl = workOrderEditPath(workOrder?.id);
  const createWoUrl = pid && projectActivity?.id ? `/dashboard/work_orders/new` : null;
  const createWoState =
    pid && projectActivity?.id
      ? {
          project: { id: pid },
          project_activity_id: projectActivity.id,
          return_after_work_order_create: activityTabUrl,
        }
      : null;

  const showActivity = schedulingMode === "project_activity" || projectActivity?.id;
  const showWo = Boolean(workOrder?.id);

  if (!showActivity && !showWo && !onEditProjectEvent) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-2.5">
      {onEditProjectEvent ? (
        <button
          type="button"
          onClick={onEditProjectEvent}
          className="inline-flex items-center gap-1.5 rounded-lg border border-teal-600 bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-teal-700"
        >
          <PencilSquareIcon className="h-4 w-4" />
          {t("project.plan.schedule.edit_project_event")}
        </button>
      ) : null}

      {showWo && woEditUrl ? (
        <Link
          to={woEditUrl}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 shadow-sm hover:border-teal-400 hover:text-teal-900"
        >
          <WrenchScrewdriverIcon className="h-4 w-4 text-teal-600" />
          {t("project.plan.schedule.open_work_order_edit")}
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 opacity-50" />
        </Link>
      ) : null}

      {showActivity && projectActivity?.id && activityTabUrl ? (
        <Link
          to={activityTabUrl}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 shadow-sm hover:border-violet-400 hover:text-violet-900"
        >
          <ClipboardDocumentListIcon className="h-4 w-4 text-violet-600" />
          {t("project.plan.schedule.open_project_activity")}
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 opacity-50" />
        </Link>
      ) : null}

      {schedulingMode === "project_activity" && !workOrder?.id && createWoUrl ? (
        <Link
          to={createWoUrl}
          state={createWoState}
          className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-teal-400 hover:text-teal-800"
        >
          <WrenchScrewdriverIcon className="h-4 w-4" />
          {t("project.plan.schedule.create_work_order_later")}
        </Link>
      ) : null}

      {onEditProjectEvent ? (
        <p className="w-full basis-full text-[11px] text-slate-500">
          {t("project.plan.schedule.edit_project_event_hint")}
        </p>
      ) : null}
    </div>
  );
}
